import { useMemo } from "react"
import { format, parse } from "date-fns"
import { skipToken } from "@reduxjs/toolkit/query"
import { useSpecialFieldsQuery } from "@/store/apis/linkApi"
import {
  GSU_PRODUCT_ID,
  WSS_PRODUCT_ID,
  productIds,
  SF_PYS_WARNING_FLAG,
  SF_PYS_CANCEL_DATE,
  SF_PYS_ELECTION_FLAG,
} from "@/utils/helpers/mappings"
import { formatSimpleDate } from "@/utils/helpers/formatters"
import { useBasicInsuranceData } from "./useInsurance"

export type PysProductId = typeof GSU_PRODUCT_ID | typeof WSS_PRODUCT_ID

type ProductKey = keyof typeof productIds

interface ProcessPysSpecialFields {
  gsu?: string
  gsu_insurance_end_date?: string
  gsu_elected?: boolean
  gsu_warning?: boolean
  wss?: string
  wss_insurance_end_date?: string
  wss_elected?: boolean
  wss_warning?: boolean
}

export interface PysSpecialField {
  data: ProcessPysSpecialFields | null | undefined
  isFetching: boolean
}

export interface PysSpecialFields {
  [GSU_PRODUCT_ID]: PysSpecialField
  [WSS_PRODUCT_ID]: PysSpecialField
  nonAffectedProductId: 50 | 70 | null
}

/**
 * Processes special fields data for Protect Your Super (PYS) insurance products
 * @param data - Array of field items containing field codes and values
 * @param productId - The product identifier (GSU or WSS)
 * @returns Processed special fields object or undefined if product not supported
 */
const processPysSpecialFields = (
  data: any,
  productId: ProductKey
): ProcessPysSpecialFields | undefined => {
  type ProductId = typeof GSU_PRODUCT_ID | typeof WSS_PRODUCT_ID
  type FieldItem = {
    fieldCode: string
    value: {
      description?: string
      code?: string
    }
  }

  const PRODUCT_CONFIG: Record<ProductId, { code: string; key: string }> = {
    [GSU_PRODUCT_ID]: { code: "gsu", key: productIds[GSU_PRODUCT_ID] },
    [WSS_PRODUCT_ID]: { code: "wss", key: productIds[WSS_PRODUCT_ID] },
  }

  const findField = (data: FieldItem[], fieldCode: string) =>
    data.find((item) => item.fieldCode === fieldCode)

  const productConfig = PRODUCT_CONFIG[productId as PysProductId]
  if (!productConfig) return undefined

  const warning = findField(data, SF_PYS_WARNING_FLAG)
  const elected = findField(data, SF_PYS_ELECTION_FLAG)
  const dateField = findField(data, SF_PYS_CANCEL_DATE)

  // Process cancellation date
  const cancelDate = dateField?.value.description
    ? formatSimpleDate(
        format(
          parse(dateField.value.description, "dd/MM/yyyy", new Date()),
          "yyyy-MM-dd"
        )
      )
    : null

  return {
    [productConfig.code]: productConfig.key,
    [`${productConfig.code}_insurance_end_date`]: cancelDate,
    [`${productConfig.code}_elected`]: elected?.value?.code === "Y",
    [`${productConfig.code}_warning`]: warning?.value?.code === "Y",
  }
}

/**
 * Hook to check if specific products have insurance coverage
 * @param productIds - Array of product IDs to check for insurance coverage
 * @returns Array of product IDs that have active insurance coverage
 */
export const usePysInsurances = (productIds: number[] = []) => {
  const PRODUCT_IDS = [GSU_PRODUCT_ID, WSS_PRODUCT_ID] as const

  // Always call hooks for both product IDs to maintain consistent hook order
  const gsuSkip = !productIds.includes(GSU_PRODUCT_ID)
  const gsuInsuranceData = useBasicInsuranceData(GSU_PRODUCT_ID, gsuSkip)

  const wssSkip = !productIds.includes(WSS_PRODUCT_ID)
  const wssInsuranceData = useBasicInsuranceData(WSS_PRODUCT_ID, wssSkip)

  const insuranceData = {
    [GSU_PRODUCT_ID]: productIds.includes(GSU_PRODUCT_ID)
      ? gsuInsuranceData.hasInsurance
      : undefined,
    [WSS_PRODUCT_ID]: productIds.includes(WSS_PRODUCT_ID)
      ? wssInsuranceData.hasInsurance
      : undefined,
  }

  return useMemo(
    () => PRODUCT_IDS.filter((productId) => insuranceData[productId] === true),
    [insuranceData]
  )
}

/**
 * Hook to fetch and process PYS (Protect Your Super) insurance data for specific member products
 * @param memberProductIds - Array of member product IDs to fetch insurance data for
 * @returns Object containing insurance data for GSU and WSS products with loading states
 */
export const usePysInsuranceData = (
  memberProductIds: number[] = []
): PysSpecialFields => {
  //const dispatch = useDispatch()
  const specialFields: PysSpecialFields = {
    [GSU_PRODUCT_ID]: { data: null, isFetching: true },
    [WSS_PRODUCT_ID]: { data: null, isFetching: true },
    nonAffectedProductId: null,
  }

  // Always call hooks to maintain consistent order - use skipToken to disable when not needed
  const gsuResponse = useSpecialFieldsQuery(
    memberProductIds.includes(GSU_PRODUCT_ID) ? GSU_PRODUCT_ID : skipToken
  )
  const wssResponse = useSpecialFieldsQuery(
    memberProductIds.includes(WSS_PRODUCT_ID) ? WSS_PRODUCT_ID : skipToken
  )

  // Process GSU data
  if (memberProductIds.includes(GSU_PRODUCT_ID)) {
    specialFields[GSU_PRODUCT_ID] = {
      data: gsuResponse.data
        ? processPysSpecialFields(gsuResponse.data, GSU_PRODUCT_ID)
        : null,
      isFetching: gsuResponse.isFetching,
    }
  } else {
    specialFields[GSU_PRODUCT_ID].isFetching = false
  }

  // Process WSS data
  if (memberProductIds.includes(WSS_PRODUCT_ID)) {
    specialFields[WSS_PRODUCT_ID] = {
      data: wssResponse.data
        ? processPysSpecialFields(wssResponse.data, WSS_PRODUCT_ID)
        : null,
      isFetching: wssResponse.isFetching,
    }
  } else {
    specialFields[WSS_PRODUCT_ID].isFetching = false
  }

  /*const payload = useMemo(
    () => ({
      [GSU_PRODUCT_ID]: {
        elected: specialFields[GSU_PRODUCT_ID].data?.elected,
        warning: specialFields[GSU_PRODUCT_ID].data?.warning,
      },
      [WSS_PRODUCT_ID]: {
        elected: specialFields[WSS_PRODUCT_ID].data?.elected,
        warning: specialFields[WSS_PRODUCT_ID].data?.warning,
      },
    }),
    [
      specialFields[GSU_PRODUCT_ID].data?.elected,
      specialFields[GSU_PRODUCT_ID].data?.warning,
      specialFields[WSS_PRODUCT_ID].data?.elected,
      specialFields[WSS_PRODUCT_ID].data?.warning,
    ]
  )

  // dispatch
  /*useEffect(() => {
    dispatch(setPysStorePayload(payload))
  }, [dispatch, payload])*/
  return specialFields
}

/**
 * Utility function to determine the non-affected product ID for PYS warnings
 * @param specialFields - The PYS special fields data
 * @param hasInsurances - Array of product IDs that have insurance
 * @returns The product ID that is not affected by warnings, or null
 */
export const getPysNonAffectedProductId = (
  specialFields: PysSpecialFields,
  hasInsurances: number[]
): PysProductId | null => {
  const wss = specialFields[WSS_PRODUCT_ID]?.data ?? {}
  const gsu = specialFields[GSU_PRODUCT_ID]?.data ?? {}

  const isWssElected = wss.wss_elected || hasInsurances.includes(WSS_PRODUCT_ID)
  const isGsuElected = gsu.gsu_elected || hasInsurances.includes(GSU_PRODUCT_ID)

  if (isWssElected && gsu.gsu_warning && !wss.wss_warning) {
    return WSS_PRODUCT_ID
  }
  if (isGsuElected && wss.wss_warning && !gsu.gsu_warning) {
    return GSU_PRODUCT_ID
  }
  return null
}

/**
 * Combined hook that provides all PYS functionality
 * @param memberProductIds - Array of member product IDs
 * @returns Object with PYS insurances, insurance data, and utility functions
 */
export const usePys = (memberProductIds: number[] = []) => {
  const hasInsurances = usePysInsurances(memberProductIds)
  const specialFields = usePysInsuranceData(memberProductIds)

  const nonAffectedProductId = getPysNonAffectedProductId(
    specialFields as PysSpecialFields,
    hasInsurances
  )

  return {
    hasInsurances,
    specialFields: {
      ...specialFields,
      nonAffectedProductId,
    },
    nonAffectedProductId,
  }
}
